import { sortColumnsByView } from 'components/basicGrid/utils';
import { modifyColumns } from 'components/basicGrid/BasicGrid.staticViews';
import { DEFAULT_PER_PAGE, GRID_IDS, PAYMENT_TYPES } from 'constants.js';

import { PAYMENTS_COLUMNS as cols, PAYMENTS_GRID_NAME } from './payments.constants';

const {
  LOAN: { LOAN_DRAW, LOAN_PAYMENT }
} = PAYMENT_TYPES;

const VIEWS = {
  ACH: [
    cols.selector,
    cols.possibleActions,
    cols.paymentType,
    cols.status,
    cols.fromAccount,
    cols.amount,
    cols.custTrace,
    cols.companyName,
    cols.achReceiver,
    cols.companyEntryDescription,
    cols.transactionDt,
    cols.numDebits,
    cols.numCredits,
    cols.activationUser,
    cols.approvalUser,
    cols.name
  ],
  LOANS: [
    cols.selector,
    cols.possibleActions,
    cols.paymentType,
    cols.status,
    cols.fromAccountName,
    cols.payeeName,
    cols.bankTrace,
    cols.amount,
    cols.transactionDt,
    cols.activationUser
  ],
  DEFAULT: [
    cols.selector,
    cols.possibleActions,
    cols.paymentType,
    cols.status,
    cols.fromAccountName,
    cols.payeeName,
    cols.custTrace,
    cols.amount,
    cols.transactionDt,
    cols.activationUser,
    cols.approvalUser,
    cols.name
  ],
  REQUIRES_MY_APPROVAL: [
    cols.selector,
    cols.possibleActions,
    cols.paymentType,
    cols.fromAccount,
    cols.payeeName,
    cols.transactionDt,
    cols.amount,
    cols.transactionDt,
    cols.amount,
    cols.activationUser,
    cols.status
  ],
  STALE_DATED: [
    cols.selector,
    cols.possibleActions,
    cols.paymentType,
    cols.fromAccountName,
    cols.payeeName,
    cols.transactionDt,
    cols.amount,
    cols.activationUser,
    cols.name,
    cols.remainingApproval
  ],
  WIRE: [
    cols.selector,
    cols.possibleActions,
    cols.paymentType,
    cols.status,
    cols.fromAccount,
    cols.beneficiary,
    cols.currency,
    cols.amount,
    cols.transactionDt,
    cols.remainingApproval,
    cols.activationUser,
    cols.approvalUser,
    cols.confirmationInfo
  ]
};

// ------------------------------------------------------------
//                 StaticViews schema details
// ------------------------------------------------------------
//                     *** primary ***
//
//            This one is used only in mobile grid.
//            All the rest applies everywhere for now.
// ------------------------------------------------------------

const staticViews = ({ active, columnDefs, locale }) => {
  const columns = modifyColumns(columnDefs);
  return {
    active,
    views: [
      {
        columns: sortColumnsByView(columns, VIEWS.DEFAULT),
        filters: [],
        page: 0,
        pageSize: DEFAULT_PER_PAGE,
        primary: [cols.payeeName, cols.amount, cols.status],
        sort: [],
        viewData: {
          id: 1,
          isDefault: true,
          isStatic: true,
          name: locale['payments.allPayments']
        }
      },
      {
        columns: sortColumnsByView(columns, VIEWS.ACH),
        filters: [
          {
            criteriaDisplay: locale['payments.filter.containsACH'],
            field: cols.paymentCategory,
            filterData: {
              filter: 'ACH',
              filterType: 'text',
              type: 'contains'
            },
            id: 'paymentCategory-contains-Contains ACH',
            nameDisplay: locale['payments.paymentCategory']
          }
        ],
        page: 0,
        pageSize: DEFAULT_PER_PAGE,
        primary: [cols.achReceiver, cols.amount, cols.transactionDt],
        sort: [],
        viewData: {
          id: 2,
          isStatic: true,
          name: locale['payments.view.ach']
        }
      },
      {
        columns: sortColumnsByView(columns, VIEWS.WIRE),
        filters: [
          {
            criteriaDisplay: locale['payments.filter.containsWire'],
            field: cols.paymentCategory,
            filterData: {
              filter: 'Wire',
              filterType: 'text',
              type: 'contains'
            },
            id: 'paymentCategory-contains-Contains Wire',
            nameDisplay: locale['payments.paymentCategory']
          }
        ],
        page: 0,
        pageSize: DEFAULT_PER_PAGE,
        primary: [cols.beneficiary, cols.amount, cols.transactionDt],
        sort: [],
        viewData: {
          id: 3,
          isStatic: true,
          name: locale['payments.view.wire']
        }
      },
      {
        columns: sortColumnsByView(columns, VIEWS.REQUIRES_MY_APPROVAL),
        filters: [
          {
            criteriaDisplay: locale['payments.filter.requiresApproval'],
            field: cols.status,
            filterData: {
              filter: 'Requires my approval',
              filterType: 'text',
              type: 'contains'
            },
            id: 'status-multi-Requires my Approval',
            nameDisplay: locale['common.status'],
            operator: 'OR'
          },
          {
            criteriaDisplay: locale['payments.filter.requiresApproval.reversal'],
            field: cols.status,
            filterData: {
              filter: 'Requires my approval Reversal',
              filterType: 'text',
              type: 'contains'
            },
            id: 'status-multi-Requires my approval Reversal',
            nameDisplay: locale['common.status'],
            operator: 'OR'
          }
        ],
        page: 0,
        pageSize: DEFAULT_PER_PAGE,
        primary: [cols.payeeName, cols.amount, cols.transactionDt],
        sort: [],
        viewData: {
          id: 4,
          isStatic: true,
          name: locale['payments.view.requiresApproval']
        }
      },
      {
        columns: sortColumnsByView(columns, VIEWS.STALE_DATED),
        filters: [
          {
            criteriaDisplay: locale['payments.filter.staleDated'],
            field: cols.status,
            filterData: {
              filter: 'Stale dated',
              filterType: 'text',
              type: 'contains'
            },
            id: 'status-multi-Stale Dated',
            nameDisplay: locale['common.status'],
            operator: 'OR'
          }
        ],
        page: 0,
        pageSize: DEFAULT_PER_PAGE,
        primary: [cols.payeeName, cols.amount, cols.transactionDt],
        sort: [],
        viewData: {
          id: 5,
          isStatic: true,
          name: locale['payments.view.staleDated']
        }
      },
      {
        columns: sortColumnsByView(columns, VIEWS.LOANS),
        filters: [
          {
            criteriaDisplay: locale['payments.filter.containsLoanPayment'],
            field: cols.paymentType,
            filterData: {
              filter: LOAN_PAYMENT,
              filterType: 'text',
              type: 'contains'
            },
            id: 'paymentType-contains-Contains Loan Payment',
            nameDisplay: locale['common.paymentType'],
            operator: 'OR'
          },
          {
            criteriaDisplay: locale['payments.filter.containsLoanDraw'],
            field: cols.paymentType,
            filterData: {
              filter: LOAN_DRAW,
              filterType: 'text',
              type: 'contains'
            },
            id: 'paymentType-contains-Contains Loan Draw',
            nameDisplay: locale['common.paymentType'],
            operator: 'OR'
          }
        ],
        page: 0,
        pageSize: DEFAULT_PER_PAGE,
        primary: [cols.payeeName, cols.amount, cols.status],
        sort: [],
        viewData: {
          id: 6,
          isStatic: true,
          name: locale['payments.view.loanPayments']
        }
      }
    ]
  };
};

// Updates storage ONLY
// savedViews: 4, still need to be passed along with history.push
// As initial load requires that, because there's no storage created yet at this point.
// --------------------------------------------------------------------------------------------------------------------------
const changePaymentsStaticView = (user, id, locale = {}) => {
  const storageKey = `${user}-${GRID_IDS.PAYMENTS_GRID}`;
  const paymentsGridStorage = JSON.parse(sessionStorage.getItem(storageKey));

  if (paymentsGridStorage) {
    const staticView = staticViews({ locale }).views.find((x) => x.viewData.id === id);
    paymentsGridStorage.savedViews.active = id;
    paymentsGridStorage.filters = staticView.filters;

    sessionStorage.setItem(storageKey, JSON.stringify(paymentsGridStorage));
  }
};

export { staticViews, changePaymentsStaticView, PAYMENTS_GRID_NAME };
